import { memo, useState, useEffect, useRef } from "react";
import { Button, Grid2, Typography, Tooltip } from "@mui/material";
import { icon } from "@/app/style/icons";
import { RouteTo } from "@routing/RouteMap";
import { availableViewportHeight } from "@core/layout/AppLayout";
import { useViewSlice } from "@store/slices/view";
import { useNavigate, useParams } from "react-router-dom";
import { DocumentSidebar } from "./documents/DocumentSidebar";
import GraphToggle from "./GraphToggle";
import GptOnlyToggle from "./GptOnlyToggle";
import { useQuery } from "@apollo/client";
import { Query } from "@/app/graphql/codegen";
import { FETCH_USER_PROFILE } from "@company/graphql/queries/fetchUserProfile";
import { useChatModes } from "@chat/hooks/useChatModes";
import { FETCH_CHAT } from "@chat/graphql/queries/fetchChat";
import { useTheme } from "@mui/material";

const ChatSidebar = memo(() => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id: chatId } = useParams<{ id: string }>();
    const { showExtendedView } = useViewSlice();
    const { isGraphEnabled, isDocumentSelectionDisabled } = useChatModes(chatId);

    const [pageQuotaLeft, setPageQuotaLeft] = useState<number>(0);
    const [totalPageQuota, setTotalPageQuota] = useState<number>(0);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const titleRef = useRef<HTMLElement>(null);

    const { data: chatData } = useQuery<Pick<Query, "chat">>(FETCH_CHAT, {
        variables: { id: chatId || "" },
        skip: !chatId,
    });

    const isGenerating = chatData?.chat.isGenerating ?? false;

    // Benutzerprofil häufiger abfragen, wenn gerade eine Antwort generiert wird
    const { data: userProfileData } = useQuery<Pick<Query, "userProfile">>(FETCH_USER_PROFILE, {
        fetchPolicy: "cache-and-network",
        pollInterval: isGenerating ? 1000 : 5000, // Poll every 1s during generation, 5s otherwise
    });

    const advancedEnabled = (userProfileData?.userProfile?.chatSettings as any)?.advancedSettingsEnabled === true;

    useEffect(() => {
        if (userProfileData?.userProfile) {
            setPageQuotaLeft((userProfileData.userProfile.pageQuota || 0) - (userProfileData.userProfile.usedPages || 0));
            setTotalPageQuota(userProfileData.userProfile.pageQuota || 0);
        }
    }, [userProfileData]);

    // Prüfen, ob der Titel abgeschnitten ist und ein Tooltip angezeigt werden soll
    useEffect(() => {
        if (titleRef.current && chatData?.chat.name) {
            const element = titleRef.current;
            const isOverflowing = element.scrollWidth > element.clientWidth;
            setShowTooltip(isOverflowing);
        }
    }, [chatData?.chat.name]);

    return (
        <Grid2 container size={12} direction={"column"} spacing={2}>
            {/* Zurück-Button */}
            <Grid2 container size={12} alignItems={"center"}>
                <Grid2>
                    <Button variant={"outlined"} startIcon={<icon.back />} onClick={() => navigate(RouteTo.CHAT)} sx={{ pr: 0.5 }} />
                </Grid2>

                <Grid2
                    size={"grow"}
                    sx={{
                        whiteSpace: "nowrap",
                        overflowX: "auto",
                        overflowY: "none",

                        "&::-webkit-scrollbar": {
                            display: "none",
                        },
                        scrollbarWidth: "none", // Für Firefox
                        msOverflowStyle: "none", // Für IE und Edge
                    }}
                >
                    <Tooltip 
                        title={chatData?.chat.name || ""} 
                        disableHoverListener={!showTooltip}
                        placement="bottom"
                    >
                        <Typography 
                            ref={titleRef}
                            variant={"h5"} 
                            align={"center"}
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                cursor: showTooltip ? "help" : "default"
                            }}
                        >
                            {chatData?.chat.name}
                        </Typography>
                    </Tooltip>
                </Grid2>
            </Grid2>

            {/* Hauptcontainer
               Seitenleiste mit festem Gesamt‑Höhenrahmen und flexiblem Dokumentenbereich.
               Der Dokumentenbereich ist der einzige Scrollbereich, damit es zu keinem
               "Wegspringen" durch Überlauf kommt. */}
            <Grid2
                container
                size={12}
                direction={"column"}
                spacing={0.25}
                height={`calc(${availableViewportHeight} - 52px)`}
                sx={{
                    border: `${showExtendedView ? 1 : 0}px solid orange`,
                    overflow: "hidden",
                    minHeight: 0,
                    minWidth: 0,
                    gridTemplateRows: "auto 1fr",
                }}
            >
                {/* Kopfbereich: Kontingent + Schalter in einer Zeile mit Auto‑Höhe */}
                <Grid2 size={12}>
                    {/* Befragbare Seiten Anzeige */}
                    <Grid2 container size={12}>
                        <Grid2 size={"grow"}>
                            <Typography variant={"body2"}>Befragbare Seiten:</Typography>
                        </Grid2>
                        <Grid2>
                            <Typography variant={"body2"}>
                                {pageQuotaLeft} / {totalPageQuota}
                            </Typography>
                        </Grid2>
                    </Grid2>

                    {/* Standard KI‑Schalter (nur bei aktivierten erweiterten Einstellungen) */}
                    {advancedEnabled && (
                        <Grid2 size={12}>
                            <GptOnlyToggle />
                        </Grid2>
                    )}
                    {/* Graph/SmartSearch‑Schalter */}
                    <Grid2 size={12}>
                        <GraphToggle />
                    </Grid2>
                </Grid2>

                {/* Dokumentenbereich: nutzt den verbleibenden Platz und enthält den einzigen Scrollbereich */}
                <Grid2 
                    size={12} 
                    mt={1} 
                    border={isGraphEnabled ? `2px solid ${theme.palette.semantic.highlight.primary}` : undefined} 
                    borderRadius={"12px"}
                    sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 0%', height: 0, minHeight: 0, minWidth: 0, overflow: 'hidden' }}
                >
                    <DocumentSidebar 
                        isGraphEnabled={isGraphEnabled} 
                        isDocumentSelectionDisabled={isDocumentSelectionDisabled} 
                    />
                </Grid2>
            </Grid2>
        </Grid2>
    );
});

export default ChatSidebar;
